import { Grid } from '@material-ui/core'
import { FC, useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { useSelector, useDispatch } from 'react-redux'
import { generalActions } from 'src/actions/GeneralActions'
import { ColorInFocus } from 'src/actions/MapActions'
import { wikiActions } from 'src/actions/WikiActions'
import { GeoModel } from 'src/dataSources/geo/GeoModel'
import { MapLegendObject } from 'src/dataSources/geo/MapLegend'
import { ImageLegend, LayerType } from 'src/dataSources/layers/LayerRegistry'
import { WikiDocumentMeta } from 'src/dataSources/wiki/Wiki'
import { RootState } from 'src/Store'
import { replaceSpecialChars } from 'src/utils/Utils'
import { layerLegendStyle } from './LayerLegend.style'

export interface LayerLegendProps {
  layerType?: LayerType
  layerPickerStyle?: boolean
  numberOfColumns?: number
  imageLegend?: ImageLegend
}

const defaultProps: LayerLegendProps = {
  layerType: undefined,
  layerPickerStyle: true,
  numberOfColumns: 2,
  imageLegend: undefined
}

export const LayerLegend: FC<LayerLegendProps> = (props: LayerLegendProps) => {
  const classes = layerLegendStyle()
  const dispatch = useDispatch()
  const intl = useIntl()

  const tokenSelector = useSelector((state: RootState) => state.authState.token)
  const allColorsInFocus = useSelector((state: RootState): ColorInFocus[] => state.mapState.colorsInFocus)
  const mapLegend = useSelector((state: RootState): MapLegendObject[] | undefined => state.generalState.mapLegend)
  const selectedGeoModel = useSelector((state: RootState): GeoModel | undefined => state.generalState.selectedGeoModel)
  const wikiDocumentsMeta = useSelector((state: RootState): WikiDocumentMeta[] => state.wikiState.documentsMeta)

  const [colorsInFocus, setColorsInFocus] = useState<string[]>([])

  const legendNameToWikiDocumentKey = (legendName: string): string => {
    return legendName.toLowerCase().replaceAll(' ', '_')
  }

  const hasDocument = (legendName: string): boolean => {
    const legendKey = legendNameToWikiDocumentKey(legendName)
    return wikiDocumentsMeta.some(meta => meta.key === legendKey)
  }

  const getImageStyle = (): React.CSSProperties|undefined => {
    if (props.imageLegend?.imageWidth !== undefined && props.imageLegend?.imageHeight !== undefined) {
      return {
        width: props.imageLegend?.imageWidth,
        height: props.imageLegend.imageHeight
      }
    }
    if (props.imageLegend?.imageWidth !== undefined) {
      return {
        width: props.imageLegend?.imageWidth
      }
    }
    if (props.imageLegend?.imageHeight !== undefined) {
      return {
        height: props.imageLegend?.imageHeight
      }
    }
    return undefined
  }

  useEffect(() => {
    if (tokenSelector === '') {
      return
    }
    if (selectedGeoModel === undefined) {
      return
    }
    dispatch(generalActions.fetchMapLegend(selectedGeoModel.ID))
    // eslint-disable-next-line
  }, [tokenSelector, selectedGeoModel])

  useEffect(() => {
    const colors: string[] = allColorsInFocus
      .filter(inFocus => inFocus.layerType === props.layerType)
      .map(inFocus => inFocus.color)
    setColorsInFocus(colors)
    // eslint-disable-next-line
  }, [allColorsInFocus])

  return (
    <div className={props.layerPickerStyle === true ? classes.root : ''}>
      {props.imageLegend === undefined && (
        <Grid container>
          {mapLegend?.map((legend, index) => {
            return (
              <Grid item xs={props.numberOfColumns === 2 ? 6 : 12} key={index}>
                <div
                  className={`${classes.layer} ${hasDocument(legend.Name) ? classes.layerWithWikiDocument : ''}`}
                  title={legend.LongName}
                  onClick={() => {
                    const legendName = replaceSpecialChars(legend.Name)
                    if (hasDocument(legendName)) {
                      dispatch(wikiActions.fetchDocument(legendNameToWikiDocumentKey(legendName)))
                    }
                  }}
                >
                  <div className={colorsInFocus.includes(legend.Color) ? classes.selectedColor : classes.color} style={{ backgroundColor: legend.Color }} />
                  <div className={colorsInFocus.includes(legend.Color) ? classes.selectedDescription : classes.description}>
                    {legend.Name}
                  </div>
                </div>
              </Grid>
            )
          })}
        </Grid>
      )}
      {props.imageLegend !== undefined && (
        <div>
          {props.imageLegend.legendTextId !== undefined && (
            <div className={classes.legendText}>{intl.formatMessage({ id: props.imageLegend.legendTextId })}</div>
          )}
          <img
            src={props.imageLegend.legendImage}
            style={getImageStyle()}
            alt={intl.formatMessage({ id: 'layerpicker.legend' })}
          />
        </div>
      )}
    </div>
  )
}

LayerLegend.defaultProps = defaultProps

export default LayerLegend
